import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Eye, CheckCircle, XCircle, AlertTriangle, Calendar, Clock, FileText, Paperclip, UserCheck, CreditCard, Package, ArrowUpDown, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { formatarMoeda } from '../utils';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';
import { useAuth } from 'contexts/AuthContext';
import Modal from 'components/common/Modal';
import { LoadingSpinner } from 'components/common';

const TabelaContasPagar = ({ 
  contasFiltradas,
  hasUnidade = false,
  loading, 
  abrirModal, 
  _temArquivos,
  contarArquivos,
  filtroAtivo,
  onPagar,
  showNotification,
  refreshKey,
  contasSelecionadas = [],
  onToggleSelecao = null,
  modoSelecao = false,
  contasEmLotes = [], // IDs de contas que já estão em lotes ativos
  onRefreshListagem = null,
  onResetVisao = null
}) => {
  const focusRingClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]';
  const statusToneClasses = {
    danger: {
      icon: 'text-[color:var(--status-danger-fg,var(--text-primary))]',
      text: 'text-[color:var(--status-danger-fg,var(--text-primary))]',
      bg: 'bg-[color:var(--status-danger-bg,var(--surface-muted))]',
      border: 'border-[color:var(--status-danger-border,var(--border))]',
    },
    warning: {
      icon: 'text-[color:var(--status-warning-fg,var(--text-primary))]',
      text: 'text-[color:var(--status-warning-fg,var(--text-primary))]',
      bg: 'bg-[color:var(--status-warning-bg,var(--surface-muted))]',
      border: 'border-[color:var(--status-warning-border,var(--border))]',
    },
    info: {
      icon: 'text-[color:var(--status-info-fg,var(--text-primary))]',
      text: 'text-[color:var(--status-info-fg,var(--text-primary))]',
      bg: 'bg-[color:var(--status-info-bg,var(--surface-muted))]',
      border: 'border-[color:var(--status-info-border,var(--border))]',
    },
    good: {
      icon: 'text-[color:var(--status-good-fg,var(--text-primary))]',
      text: 'text-[color:var(--status-good-fg,var(--text-primary))]',
      bg: 'bg-[color:var(--status-good-bg,var(--surface-muted))]',
      border: 'border-[color:var(--status-good-border,var(--border))]',
    },
    neutral: {
      icon: 'text-[color:var(--text-muted)]',
      text: 'text-[color:var(--text-muted)]',
      bg: 'bg-[color:var(--surface-muted)]',
      border: 'border-[color:var(--border)]',
    },
  };
  const [processandoPagamento, setProcessandoPagamento] = useState({});
  const [authorizations, setAuthorizations] = useState({});
  const [loadingAuthorizations, setLoadingAuthorizations] = useState({});
  const [contaParaAutorizar, setContaParaAutorizar] = useState(null);
  const [desktopSort, setDesktopSort] = useState({ field: null, direction: 'asc' });
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const showUnidade = !!hasUnidade;
  const contasEmLotesNormalizadas = useMemo(() => {
    if (Array.isArray(contasEmLotes)) {
      return contasEmLotes;
    }
    if (contasEmLotes === null || contasEmLotes === undefined) {
      return [];
    }
    return [contasEmLotes];
  }, [contasEmLotes]);
  const contasEmLotesSet = useMemo(
    () => new Set(contasEmLotesNormalizadas.map((id) => String(id))),
    [contasEmLotesNormalizadas]
  );
  const contaEstaEmLote = (id) => contasEmLotesSet.has(String(id));
  const contasSelecionadasSet = useMemo(
    () => new Set(contasSelecionadas.map((id) => String(id))),
    [contasSelecionadas]
  );
  const contasSelecionaveisFiltradas = useMemo(
    () => contasFiltradas.filter((conta) => !contasEmLotesSet.has(String(conta.id))),
    [contasFiltradas, contasEmLotesSet]
  );
  const todasContasSelecionaveisSelecionadas =
    contasSelecionaveisFiltradas.length > 0 &&
    contasSelecionaveisFiltradas.every((conta) => contasSelecionadasSet.has(String(conta.id)));

  const handleSelecionarTodasFiltradas = (checked) => {
    if (!onToggleSelecao) return;

    contasSelecionaveisFiltradas.forEach((conta) => {
      const estaSelecionada = contasSelecionadasSet.has(String(conta.id));
      if ((checked && !estaSelecionada) || (!checked && estaSelecionada)) {
        onToggleSelecao(conta.id);
      }
    });
  };
  
  // Verificar se o usuário tem permissão para autorizar pagamentos
  const canAuthorizePayment = hasPermission('administrativo.pagamento.authorize');
  const formatarPrazoVencimento = (diasVencimento, status) => {
    if (status === 'paga' || status === 'cancelada' || status === 'paga_parcialmente') return '';
    if (!Number.isFinite(diasVencimento)) return 'Sem data';
    if (diasVencimento < 0) {
      const dias = Math.abs(diasVencimento);
      return `${dias} ${dias === 1 ? 'dia atrasado' : 'dias atrasados'}`;
    }
    if (diasVencimento === 0) return 'Vence hoje';
    return `Em ${diasVencimento} ${diasVencimento === 1 ? 'dia' : 'dias'}`;
  };

  const handleDesktopSort = (field) => {
    setDesktopSort((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      return { field: null, direction: 'asc' };
    });
  };

  const getVencimentoSeguro = (conta) => {
    const base = conta?.data_vencimento_convertida || conta?.dataVencimento || conta?.data_vencimento;
    if (!base) return null;
    if (base instanceof Date) {
      return Number.isNaN(base.getTime()) ? null : new Date(base);
    }
    const parsed = new Date(base);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getSortValue = useCallback((conta, field) => {
    switch (field) {
      case 'fornecedor':
        return (conta.fornecedor || '').toLowerCase();
      case 'descricao':
        return (conta.observacao || conta.descricao || '').toLowerCase();
      case 'valor':
        return parseFloat(conta.valor) || 0;
      case 'vencimento':
        return getVencimentoSeguro(conta)?.getTime() || 0;
      case 'status':
        return (conta.status || '').toLowerCase();
      default:
        return '';
    }
  }, []);

  const contasExibicao = useMemo(() => {
    if (!desktopSort.field) return contasFiltradas;
    const contasOrdenadas = [...contasFiltradas];
    contasOrdenadas.sort((a, b) => {
      const valueA = getSortValue(a, desktopSort.field);
      const valueB = getSortValue(b, desktopSort.field);
      if (valueA === valueB) return 0;
      const direction = desktopSort.direction === 'asc' ? 1 : -1;
      return valueA > valueB ? direction : -direction;
    });
    return contasOrdenadas;
  }, [contasFiltradas, desktopSort, getSortValue]);

  const getHeaderSortIcon = (field) => {
    if (desktopSort.field !== field) return <ArrowUpDown className="h-3 w-3 opacity-70" />;
    if (desktopSort.direction === 'asc') return <ChevronUp className="h-3 w-3" />;
    return <ChevronDown className="h-3 w-3" />;
  };

  const getAriaSort = (field) => {
    if (desktopSort.field !== field) return 'none';
    return desktopSort.direction === 'asc' ? 'ascending' : 'descending';
  };

  const emptyStateContent = useMemo(() => {
    if (filtroAtivo === 'prontas_para_pagar') {
      return {
        title: 'Nenhuma conta pronta para pagar',
        description: 'Nenhuma conta atende os critérios atuais para autorização.',
      };
    }
    if (filtroAtivo === 'pagas') {
      return {
        title: 'Nenhuma conta paga encontrada',
        description: 'Ajuste os filtros para visualizar pagamentos já processados.',
      };
    }
    if (filtroAtivo === 'vencidas') {
      return {
        title: 'Nenhuma conta vencida encontrada',
        description: 'Não há contas vencidas com os filtros aplicados.',
      };
    }
    return {
      title: 'Nenhuma conta encontrada',
      description: 'Ajuste os filtros ou atualize a listagem.',
    };
  }, [filtroAtivo]);
  // Funções de estilo para status (baseadas na versão anterior)
  const getStatusInfo = (status) => {
    switch (status) {
      case 'vencida': {
        const tone = statusToneClasses.danger;
        return {
          icon: <XCircle className={`h-4 w-4 ${tone.icon}`} />,
          label: 'Vencida',
          color: tone.text,
          bgColor: tone.bg,
          borderColor: tone.border
        };
      }
      case 'proxima_vencimento': {
        const warningTone = statusToneClasses.warning;
        return {
          icon: <AlertTriangle className={`h-4 w-4 ${warningTone.icon}`} />,
          label: 'Próxima a vencer',
          color: warningTone.text,
          bgColor: warningTone.bg,
          borderColor: warningTone.border
        };
      }
      case 'hoje': {
        const todayTone = statusToneClasses.warning;
        return {
          icon: <Calendar className={`h-4 w-4 ${todayTone.icon}`} />,
          label: 'Vence hoje',
          color: todayTone.text,
          bgColor: todayTone.bg,
          borderColor: todayTone.border
        };
      }
      case 'proximos_meses': {
        const infoTone = statusToneClasses.info;
        return {
          icon: <Clock className={`h-4 w-4 ${infoTone.icon}`} />,
          label: 'Próximos meses',
          color: infoTone.text,
          bgColor: infoTone.bg,
          borderColor: infoTone.border
        };
      }
      case 'paga': {
        const goodTone = statusToneClasses.good;
        return {
          icon: <CheckCircle className={`h-4 w-4 ${goodTone.icon}`} />,
          label: 'Paga',
          color: goodTone.text,
          bgColor: goodTone.bg,
          borderColor: goodTone.border
        };
      }
      case 'cancelada':
        return {
          icon: <XCircle className={`h-4 w-4 ${statusToneClasses.neutral.icon}`} />,
          label: 'Cancelada',
          color: statusToneClasses.neutral.text,
          bgColor: statusToneClasses.neutral.bg,
          borderColor: statusToneClasses.neutral.border
        };
      case 'paga_parcialmente': {
        const partialTone = statusToneClasses.info;
        return {
          icon: <Clock className={`h-4 w-4 ${partialTone.icon}`} />,
          label: 'Paga Parcial',
          color: partialTone.text,
          bgColor: partialTone.bg,
          borderColor: partialTone.border
        };
      }
      default: {
        const defaultTone = statusToneClasses.good;
        return {
          icon: <CheckCircle className={`h-4 w-4 ${defaultTone.icon}`} />,
          label: 'Em dia',
          color: defaultTone.text,
          bgColor: defaultTone.bg,
          borderColor: defaultTone.border
        };
      }
    }
  };

  const renderArquivos = (conta) => {
    const quantidadeArquivos = contarArquivos(conta);
    const labelArquivos = quantidadeArquivos === 0
      ? 'Sem arquivos'
      : `${quantidadeArquivos} ${quantidadeArquivos === 1 ? 'arquivo' : 'arquivos'}`;
    return (
      <div className="flex items-center justify-center">
        <span
          className="inline-flex min-h-6 min-w-[56px] items-center justify-center gap-1 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--text-muted)]"
          title={labelArquivos}
          aria-label={labelArquivos}
        >
          {quantidadeArquivos > 0 ? (
            <Paperclip className="h-3.5 w-3.5 text-[color:var(--accent)]" />
          ) : (
            <FileText className="h-3.5 w-3.5 text-[color:var(--text-muted)]" />
          )}
          <span>{quantidadeArquivos}</span>
        </span>
      </div>
    );
  };

  // Função para obter identificador de transação baseado no tipo de pagamento
  // Usa campos diretos da conta: chave_pix para PIX e codigo_barras para Boleto
  const getTransactionIdentifier = (conta) => {
    const tipoPagamento = (conta.tipo_pagamento || '').toLowerCase().trim();

    if (tipoPagamento.includes('pix')) {
      // Para PIX: usar chave_pix diretamente
      return {
        identifier: conta.chave_pix,
        type: 'pix',
        fieldName: 'chave_pix'
      };
    } else if (tipoPagamento.includes('boleto')) {
      // Para Boleto: usar código de barras
      return {
        identifier: conta.codigo_barras,
        type: 'boleto',
        fieldName: 'codigo_barras'
      };
    } else if (tipoPagamento.includes('cartao') || tipoPagamento.includes('cartão')) {
      // Para Cartão: não há campo específico ainda - retornar null por enquanto
      return {
        identifier: null,
        type: 'cartao',
        fieldName: 'transactionIdentifier'
      };
    } else if (tipoPagamento.includes('transferencia') || tipoPagamento.includes('transferência')) {
      // Para Transferência: não há campo específico ainda - retornar null por enquanto
      return {
        identifier: null,
        type: 'transferencia',
        fieldName: 'transactionIdentifier'
      };
    }
    
    return {
      identifier: null,
      type: null,
      fieldName: null
    };
  };

  const getPaymentProviderLabel = (conta) => {
    const contaNome = (conta?.conta || '').toString().toLowerCase();

    if (contaNome.includes('bradesco')) return 'Bradesco';
    if (contaNome.includes('modobank') || contaNome.includes('modo bank')) return 'ModoBank';
    if (contaNome.includes('c6')) return 'C6 Bank';
    if (contaNome.includes('santander')) return 'Santander';
    if (contaNome.includes('itau') || contaNome.includes('itaú')) return 'Itaú';
    if (contaNome.includes('banco do brasil') || contaNome.includes('bb')) return 'Banco do Brasil';

    return 'Banco configurado';
  };

  const getAuthorizationCompactInfo = (authorization) => {
    if (!authorization) {
      const tone = statusToneClasses.neutral;
      return {
        label: 'Pendente',
        tone,
        icon: <Clock className={`h-3 w-3 ${tone.icon}`} />,
        details: 'Autorização ainda não iniciada para esta conta.'
      };
    }

    if (authorization.status === 'processed') {
      const tone = statusToneClasses.good;
      return {
        label: 'Processado',
        tone,
        icon: <CheckCircle className={`h-3 w-3 ${tone.icon}`} />,
        details: 'Pagamento já processado.'
      };
    }

    if (authorization.status === 'cancelled') {
      const tone = statusToneClasses.danger;
      return {
        label: 'Falhou',
        tone,
        icon: <XCircle className={`h-3 w-3 ${tone.icon}`} />,
        details: authorization.error_message || 'Falha no processamento da autorização.'
      };
    }

    if (authorization.needs_second_authorization) {
      const tone = statusToneClasses.warning;
      const firstAuthorizerName = authorization.first_authorizer?.name;
      return {
        label: 'Aguardando 2ª',
        tone,
        icon: <Clock className={`h-3 w-3 ${tone.icon}`} />,
        details: firstAuthorizerName
          ? `Primeira autorização por ${firstAuthorizerName}. Aguardando segunda aprovação.`
          : 'Primeira autorização concluída. Aguardando segunda aprovação.'
      };
    }

    if (authorization.first_authorizer) {
      const tone = statusToneClasses.info;
      return {
        label: '1ª aprovada',
        tone,
        icon: <UserCheck className={`h-3 w-3 ${tone.icon}`} />,
        details: `Aprovador: ${authorization.first_authorizer.name || 'N/A'}.`
      };
    }

    const tone = statusToneClasses.neutral;
    return {
      label: 'Pendente',
      tone,
      icon: <Clock className={`h-3 w-3 ${tone.icon}`} />,
      details: 'Autorização pendente.'
    };
  };

  // Função para buscar status de autorização
  const fetchAuthorizationStatus = async (conta) => {
    if (!conta.id) return;
    
    const key = `conta_${conta.id}`;
    if (loadingAuthorizations[key]) return; // Evitar múltiplas requisições
    
    setLoadingAuthorizations(prev => ({ ...prev, [key]: true }));

    try {
      const response = await api.get('/n8n/administracao/a_pagar/authorization-status', {
        params: {
          conta_pagar_id: conta.id,
          _t: Date.now(), // evita resposta GET cacheada/stale
        }
      });
      
      if (response.data.success) {
        // Importante: quando authorization vier null, precisamos limpar o estado local
        // para não manter erro antigo "fantasma" na UI.
        setAuthorizations(prev => ({
          ...prev,
          [conta.id]: response.data.authorization ?? null
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar status de autorização:', error);
    } finally {
      setLoadingAuthorizations(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }
  };

  // Carregar status de autorização para contas (apenas na subaba prontas_para_pagar)
  useEffect(() => {
    if (filtroAtivo === 'prontas_para_pagar' && contasFiltradas.length > 0) {
      // Se refreshKey mudou, limpar cache para forçar recarregamento de todos os status
      if (refreshKey > 0) {
        setAuthorizations({});
      }
      
      contasFiltradas.forEach(conta => {
        // Buscar status para todas as contas (não precisa mais validar transactionInfo)
        if (refreshKey > 0 || !authorizations[conta.id]) {
          fetchAuthorizationStatus(conta);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroAtivo, contasFiltradas.length, refreshKey]);

  // Função para processar pagamento (sem código SMS)
  const handlePagar = async (conta) => {
    setProcessandoPagamento(prev => ({ ...prev, [conta.id]: true }));

    try {
      // Chamar diretamente o endpoint de pagamento (sem código SMS)
      const response = await api.post('/n8n/administracao/a_pagar/pagar', {
        conta_pagar_id: conta.id,
        notes: null
      });

      if (response.data.success) {
        // Atualizar status de autorização localmente
        if (response.data.authorization) {
          const nextAuthorization = { ...response.data.authorization };
          if (nextAuthorization.status !== 'cancelled') {
            nextAuthorization.error_message = null;
            nextAuthorization.failed_at = null;
            nextAuthorization.payment_result = null;
          }

          setAuthorizations(prev => ({
            ...prev,
            [conta.id]: nextAuthorization
          }));
        }

        if (response.data.authorization?.needs_second_authorization) {
          if (showNotification) {
            showNotification('Primeira autorização registrada. Aguardando segunda autorização.', 'info');
          }
        } else {
          if (showNotification) {
            showNotification('Pagamento processado com sucesso!', 'success');
          }
          // Recarregar dados se pagamento foi processado
          if (onPagar) {
            await onPagar();
          }
        }

        // Sincroniza estado final após autorização/pagamento para evitar UI com status antigo
        await fetchAuthorizationStatus(conta);
      } else {
        if (showNotification) {
          showNotification(response.data.message || 'Erro ao processar pagamento', 'error');
        }
        // Buscar status atualizado após erro para permitir nova tentativa
        await fetchAuthorizationStatus(conta);
      }
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      if (showNotification) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao processar pagamento';
        showNotification(errorMessage, 'error');
      }
      // Buscar status atualizado após erro para permitir nova tentativa
      await fetchAuthorizationStatus(conta);
    } finally {
      setProcessandoPagamento(prev => {
        const newState = { ...prev };
        delete newState[conta.id];
        return newState;
      });
    }
  };

  // Função para abrir modal de confirmação
  const abrirModalConfirmacao = (conta) => {
    const transactionInfo = getTransactionIdentifier(conta);
    const authorization = authorizations[conta.id];
    const isSameUserFromFirstAuthorization =
      !!authorization?.needs_second_authorization &&
      !authorization?.second_authorizer &&
      authorization?.first_authorizer?.id === user?.id;
    
    // Validar antes de abrir o modal
    if (!transactionInfo.identifier || !transactionInfo.type) {
      if (showNotification) {
        const fieldName = transactionInfo.fieldName || 'identificador';
        showNotification(`Dados incompletos para autorizar pagamento. Falta o ${fieldName}.`, 'error');
      }
      return;
    }

    if (isSameUserFromFirstAuthorization) {
      if (showNotification) {
        showNotification('Você realizou a 1ª autorização deste pagamento e não pode realizar a 2ª autorização.', 'warning');
      }
      return;
    }
    
    setContaParaAutorizar(conta);
  };

  // Função para fechar modal de confirmação
  const fecharModalConfirmacao = () => {
    setContaParaAutorizar(null);
  };

  // Função para confirmar e processar pagamento
  const confirmarAutorizacao = async () => {
    if (!contaParaAutorizar) return;
    
    await handlePagar(contaParaAutorizar);
    fecharModalConfirmacao();
  };

  // Função para renderizar card mobile
  const renderCardMobile = (conta) => {
    const hoje = new Date();
    const vencimento = getVencimentoSeguro(conta);
    hoje.setHours(0, 0, 0, 0);
    const vencimentoNormalizado = vencimento ? new Date(vencimento) : null;
    vencimentoNormalizado?.setHours(0, 0, 0, 0);
    const diasVencimento = vencimentoNormalizado
      ? Math.floor((vencimentoNormalizado - hoje) / (1000 * 60 * 60 * 24))
      : Number.POSITIVE_INFINITY;
    
    const status = conta.status;
    
    const statusInfo = getStatusInfo(status, diasVencimento);
    const transactionInfo = filtroAtivo === 'prontas_para_pagar' ? getTransactionIdentifier(conta) : null;
    const estaProcessando = processandoPagamento[conta.id] || false;
    const authorization = filtroAtivo === 'prontas_para_pagar' ? authorizations[conta.id] : null;
    const isSameUserFromFirstAuthorization =
      !!authorization?.needs_second_authorization &&
      !authorization?.second_authorizer &&
      authorization?.first_authorizer?.id === user?.id;

    const isFinalizada = status === 'paga' || status === 'paga_parcialmente' || status === 'cancelada';
    const dueLabel = isFinalizada
      ? (status === 'paga' || status === 'paga_parcialmente' ? 'Pago' : 'Cancelado')
      : formatarPrazoVencimento(diasVencimento, status);
    const dueColor = isFinalizada
      ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'
      : (diasVencimento < 0
        ? 'bg-[color:var(--status-danger-bg,var(--surface-muted))] text-[color:var(--status-danger-fg,var(--text-primary))]'
        : diasVencimento <= 7
        ? 'bg-[color:var(--status-warning-bg,var(--surface-muted))] text-[color:var(--status-warning-fg,var(--text-primary))]'
        : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]');

    const abrirDetalhesConta = () => {
      if (!modoSelecao) {
        abrirModal(conta);
      }
    };

    const handleCardKeyDown = (event) => {
      if (modoSelecao) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        abrirDetalhesConta();
      }
    };

    return (
      <div
        key={conta.id}
        role={modoSelecao ? undefined : 'button'}
        tabIndex={modoSelecao ? -1 : 0}
        onClick={abrirDetalhesConta}
        onKeyDown={handleCardKeyDown}
        className={`group mb-3 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-sm transition-colors ${
          modoSelecao
            ? ''
            : 'cursor-pointer hover:bg-[color:var(--surface-muted)]/35 hover:border-[color:var(--accent)]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35'
        }`}
        aria-label={modoSelecao ? undefined : `Abrir detalhes da conta ${conta.id}`}
      >
        {/* Linha 1: fornecedor + valor */}
        <div className="flex items-start justify-between gap-3 mb-2">
          {modoSelecao && (
            <div className="mr-2 mt-1">
              <input
                type="checkbox"
                checked={contasSelecionadas.includes(conta.id)}
                onChange={() => onToggleSelecao && onToggleSelecao(conta.id)}
                onClick={(event) => event.stopPropagation()}
                disabled={contaEstaEmLote(conta.id)}
                className={`h-4 w-4 accent-[color:var(--accent)] border-[color:var(--border)] rounded disabled:opacity-50 disabled:cursor-not-allowed ${focusRingClass}`}
                title={contaEstaEmLote(conta.id) ? 'Esta conta já está em um lote ativo' : ''}
              />
            </div>
          )}
	          <div className="flex-1 min-w-0">
	            <div className="flex items-center gap-2 mb-1">
	              <span className="font-mono text-[10px] text-[color:var(--text-muted)]">#{conta.id}</span>
	              {conta.unidade && (
	                <span
	                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] border border-[color:var(--border)]"
	                  title={`Unidade: ${conta.unidade}`}
	                >
	                  {conta.unidade}
	                </span>
	              )}
	              {modoSelecao && contaEstaEmLote(conta.id) && (
		                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[color:var(--status-warning-bg,var(--surface-muted))] text-[color:var(--status-warning-fg,var(--text-primary))] border border-[color:var(--status-warning-border,var(--border))]" title="Esta conta já está em um lote ativo">
		                  <Package className="h-3 w-3 mr-1" />
		                  Em Lote
		                </span>
	              )}
            </div>
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)] truncate">{conta.fornecedor}</h3>
          </div>
          <div className="text-right">
            <p className="text-base font-semibold text-[color:var(--text-primary)]">
              {formatarMoeda(parseFloat(conta.valor) || 0)}
            </p>
          </div>
        </div>

        {/* Linha 2: vencimento + status em blocos separados */}
        <div className="mb-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs text-[color:var(--text-muted)]">
              Vencimento:{' '}
              <span className="text-[color:var(--text-primary)] font-medium">
                {vencimento ? vencimento.toLocaleDateString('pt-BR') : 'Data inválida'}
              </span>
            </div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${dueColor}`}>
              {dueLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor} border`}>
              {statusInfo.icon}
              <span className="ml-1">{statusInfo.label}</span>
            </span>
            {filtroAtivo === 'prontas_para_pagar' && authorization && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]">
                {authorization.second_authorizer
                  ? '2ª autorização'
                  : authorization.first_authorizer
                  ? '1ª autorização'
                  : 'Sem autorização'}
              </span>
            )}
          </div>
        </div>

        {filtroAtivo === 'prontas_para_pagar' && authorization?.status === 'cancelled' && (
          <div className="mb-2 rounded border border-[color:var(--status-danger-border,var(--border))] bg-[color:var(--status-danger-bg,var(--surface-muted))] p-2">
            <div className="flex items-center gap-1 text-xs font-semibold text-[color:var(--status-danger-fg,var(--text-primary))]">
              <XCircle className="h-3 w-3" />
              Falhou
            </div>
            {authorization.error_message && (
              <p className="mt-1 text-xs text-[color:var(--status-danger-fg,var(--text-primary))] break-words">
                {authorization.error_message}
              </p>
            )}
            {authorization.failed_at && (
              <p className="mt-1 text-[10px] text-[color:var(--text-muted)]">
                {new Date(authorization.failed_at).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        )}

        {/* Linha 3: categoria + conta */}
        <div className="text-xs text-[color:var(--text-muted)] mb-2 line-clamp-2">
          {conta.planejamento || 'Sem planejamento'} › {conta.conta_analitica || 'Sem subcategoria'}
          {conta.conta && (
            <span className="ml-2 text-[color:var(--text-muted)]">• {conta.conta}</span>
          )}
        </div>

        {/* Descrição */}
        <p className="text-xs text-[color:var(--text-primary)] mb-2 line-clamp-2">
          {conta.observacao || conta.descricao}
        </p>

	        {/* Rodapé: ações e status auditoria */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[color:var(--border)]">
          <div className="flex items-center gap-1 text-[11px] font-medium text-[color:var(--text-muted)]">
            {!modoSelecao && (
              <>
                <span>Toque para detalhes</span>
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-auto">
            {renderArquivos(conta)}
            <button 
              onClick={(event) => {
                event.stopPropagation();
                abrirModal(conta);
              }}
              className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-[color:var(--accent)] hover:text-[color:var(--text-primary)] rounded-md hover:bg-[color:var(--surface-muted)] ${focusRingClass}`}
              title="Visualizar"
              aria-label={`Visualizar conta ${conta.id}`}
            >
              <Eye className="h-4 w-4" />
            </button>
            {filtroAtivo === 'prontas_para_pagar' && conta.status !== 'paga' && conta.status !== 'cancelada' && conta.status !== 'paga_parcialmente' && canAuthorizePayment && (
              <button 
                onClick={(event) => {
                  event.stopPropagation();
                  if (transactionInfo?.identifier) {
                    abrirModalConfirmacao(conta);
                  }
                }}
                disabled={
                  estaProcessando || 
                  authorization?.status === 'processed' ||
                  !transactionInfo?.identifier ||
                  isSameUserFromFirstAuthorization
                }
                className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md ${
                  isSameUserFromFirstAuthorization
                    ? 'text-[color:var(--text-muted)] bg-[color:var(--surface-muted)] cursor-not-allowed'
                    : transactionInfo?.identifier
                    ? 'text-[color:var(--status-good-fg,var(--text-primary))] hover:bg-[color:var(--status-good-bg,var(--surface-muted))]'
                    : 'text-[color:var(--text-muted)]'
                } ${estaProcessando || authorization?.status === 'processed' ? 'opacity-50 cursor-not-allowed' : ''} ${focusRingClass}`}
                title={isSameUserFromFirstAuthorization ? 'Você fez a 1ª autorização e não pode fazer a 2ª' : 'Pagar'}
                aria-label={`Autorizar pagamento da conta ${conta.id}`}
              >
                {estaProcessando ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
              </button>
            )}
	          </div>
	        </div>
	      </div>
	    );
	  };

  return (
    <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg overflow-hidden relative">
      {!loading && contasFiltradas.length === 0 && (
        <div className="p-8 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]">
            <FileText className="h-5 w-5" />
          </div>
          <p className="text-sm font-medium text-[color:var(--text-primary)]">{emptyStateContent.title}</p>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">{emptyStateContent.description}</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {onResetVisao && filtroAtivo !== 'todas' && (
              <button
                type="button"
                onClick={onResetVisao}
                className={`inline-flex min-h-[44px] items-center justify-center rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] ${focusRingClass}`}
              >
                Ver todas
              </button>
            )}
            {onRefreshListagem && (
              <button
                type="button"
                onClick={onRefreshListagem}
                className={`inline-flex min-h-[44px] items-center justify-center rounded-md bg-[color:var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 ${focusRingClass}`}
              >
                Atualizar
              </button>
            )}
          </div>
        </div>
      )}
	      {/* Versão Desktop - Tabela */}
	      <div className="hidden md:block overflow-auto max-h-[32rem]">
	        <table className="w-full min-w-[920px] table-fixed divide-y divide-[color:var(--border)]" style={{ tableLayout: 'fixed', width: '100%' }}>
	          <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
	            <tr>
              {modoSelecao && (
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[4%]">
                  <input
                    type="checkbox"
                    checked={todasContasSelecionaveisSelecionadas}
                    onChange={(e) => handleSelecionarTodasFiltradas(e.target.checked)}
                    className={`h-4 w-4 accent-[color:var(--accent)] border-[color:var(--border)] rounded ${focusRingClass}`}
                  />
                </th>
              )}
	              <th className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[6%]">
	                ID
	              </th>
	              {showUnidade && (
	                <th className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[10%]">
	                  Unidade
	                </th>
	              )}
	              <th aria-sort={getAriaSort('fornecedor')} className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[18%]">
                  <button type="button" onClick={() => handleDesktopSort('fornecedor')} className={`inline-flex items-center gap-1 ${focusRingClass}`}>
                    Fornecedor
                    {getHeaderSortIcon('fornecedor')}
                  </button>
	              </th>
              <th aria-sort={getAriaSort('descricao')} className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[23%]">
                <button type="button" onClick={() => handleDesktopSort('descricao')} className={`inline-flex items-center gap-1 ${focusRingClass}`}>
                  Descrição
                  {getHeaderSortIcon('descricao')}
                </button>
              </th>
              <th aria-sort={getAriaSort('valor')} className="px-3 py-2 text-right text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[11%]">
                <button type="button" onClick={() => handleDesktopSort('valor')} className={`ml-auto inline-flex items-center gap-1 ${focusRingClass}`}>
                  Valor
                  {getHeaderSortIcon('valor')}
                </button>
              </th>
              <th aria-sort={getAriaSort('vencimento')} className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[13%]">
                <button type="button" onClick={() => handleDesktopSort('vencimento')} className={`inline-flex items-center gap-1 ${focusRingClass}`}>
                  {filtroAtivo === 'pagas' ? 'Data de Pagamento' : 'Vencimento'}
                  {getHeaderSortIcon('vencimento')}
                </button>
              </th>
              <th aria-sort={getAriaSort('status')} className="px-3 py-2 text-center text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-[0.08em] w-[11%]">
                <button type="button" onClick={() => handleDesktopSort('status')} className={`mx-auto inline-flex items-center gap-1 ${focusRingClass}`}>
                  Status
                  {getHeaderSortIcon('status')}
                </button>
              </th>
              {filtroAtivo === 'prontas_para_pagar' && (
                <th className="px-3 py-2 text-left text-[11px] font-semibold text-[color:var(--text-muted)] tracking-[0.04em] w-[12%]">
                  Autorização
                </th>
              )}
	              <th className="px-2 py-2 text-center text-[11px] font-semibold text-[color:var(--text-muted)] tracking-[0.04em] w-[68px]">
	                Arquivos
	              </th>
              {filtroAtivo === 'prontas_para_pagar' && (
	                <th className="px-2 py-2 text-center text-[11px] font-semibold text-[color:var(--text-muted)] tracking-[0.04em] w-[92px]">
	                  Ações
	                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
            {contasExibicao.map((conta) => {
              // Calcular dias de vencimento apenas para exibição
              const hoje = new Date();
              const vencimento = getVencimentoSeguro(conta);
              hoje.setHours(0, 0, 0, 0);
              const vencimentoNormalizado = vencimento ? new Date(vencimento) : null;
              vencimentoNormalizado?.setHours(0, 0, 0, 0);
              const diasVencimento = vencimentoNormalizado
                ? Math.floor((vencimentoNormalizado - hoje) / (1000 * 60 * 60 * 24))
                : Number.POSITIVE_INFINITY;
              
              // Usar o status já processado em processarContas.js
              const status = conta.status;
              
              const statusInfo = getStatusInfo(status, diasVencimento);
              
              // Obter identificador de transação (apenas para prontas_para_pagar)
              const transactionInfo = filtroAtivo === 'prontas_para_pagar' ? getTransactionIdentifier(conta) : null;
              const estaProcessando = processandoPagamento[conta.id] || false;
              
              // Obter status de autorização
              const authorization = filtroAtivo === 'prontas_para_pagar' ? authorizations[conta.id] : null;
              const compactAuthorizationInfo = filtroAtivo === 'prontas_para_pagar' ? getAuthorizationCompactInfo(authorization) : null;
              const isSameUserFromFirstAuthorization =
                !!authorization?.needs_second_authorization &&
                !authorization?.second_authorizer &&
                authorization?.first_authorizer?.id === user?.id;
              
              return (
                <tr
                  key={conta.id}
                  tabIndex={modoSelecao ? -1 : 0}
                  role={modoSelecao ? undefined : 'button'}
                  aria-label={modoSelecao ? undefined : `Abrir detalhes da conta ${conta.id}`}
                  onClick={modoSelecao ? undefined : () => abrirModal(conta)}
                  onKeyDown={
                    modoSelecao
                      ? undefined
                      : (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            abrirModal(conta);
                          }
                        }
                  }
                  className={`group transition-colors duration-200 odd:bg-[color:var(--surface)] even:bg-[color:var(--surface-muted)]/35 hover:bg-[color:var(--surface-muted)] focus-within:bg-[color:var(--surface-muted)] focus-visible:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--accent)] ${
                    modoSelecao ? '' : 'cursor-pointer'
                  }`}
                >
                  {modoSelecao && (
                    <td className="px-3 py-2.5 align-top">
                      <input
                        type="checkbox"
                        checked={contasSelecionadas.includes(conta.id)}
                        onChange={() => onToggleSelecao && onToggleSelecao(conta.id)}
                        onClick={(event) => event.stopPropagation()}
                        disabled={contaEstaEmLote(conta.id)}
                        className={`h-4 w-4 accent-[color:var(--accent)] border-[color:var(--border)] rounded disabled:opacity-50 disabled:cursor-not-allowed ${focusRingClass}`}
                        title={contaEstaEmLote(conta.id) ? 'Esta conta já está em um lote ativo' : ''}
                      />
                    </td>
                  )}
	                  <td className="px-3 py-2.5 align-top">
	                    <span className="font-mono text-xs text-[color:var(--text-muted)]">#{conta.id}</span>
	                    {/* Badge removido da versão desktop - checkbox desabilitado já é indicador visual suficiente */}
	                  </td>
	                  {showUnidade && (
	                    <td className="px-3 py-2.5 align-top">
	                      <div className="text-xs text-[color:var(--text-primary)] truncate" title={conta.unidade || ''}>
	                        {conta.unidade || '-'}
	                      </div>
	                    </td>
	                  )}
	                  <td className="px-3 py-2.5 align-top">
	                    <div className="text-sm font-semibold leading-5 text-[color:var(--text-primary)] truncate" title={conta.fornecedor}>
	                      {conta.fornecedor}
	                    </div>
                    <div className="mt-0.5 text-xs text-[color:var(--text-muted)] truncate" title={`${conta.planejamento || 'Sem planejamento'} › ${conta.conta_analitica || 'Sem subcategoria'}`}>
                      {(conta.planejamento || 'Sem planejamento')} <span className="text-[color:var(--text-muted)]">›</span> {(conta.conta_analitica || 'Sem subcategoria')}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <div className="text-sm leading-5 text-[color:var(--text-primary)] break-words max-h-10 overflow-hidden" title={conta.observacao || conta.descricao}>
                      {conta.observacao || conta.descricao}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <div className="text-sm font-semibold tabular-nums text-[color:var(--text-primary)] text-right">
                      {formatarMoeda(parseFloat(conta.valor) || 0)}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    {filtroAtivo === 'pagas' ? (
                      <div className="text-sm tabular-nums text-[color:var(--text-primary)]">
                        {(() => {
                          if (!conta.data_pagamento) return 'N/A';
                          try {
                            const dataStr = String(conta.data_pagamento).trim();
                            const matchBrasileiro = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
                            if (matchBrasileiro) {
                              const [, dia, mes, ano, hora = '00', minuto = '00'] = matchBrasileiro;
                              const dateObj = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}`);
                              if (!isNaN(dateObj.getTime())) {
                                const dataFormatada = dateObj.toLocaleDateString('pt-BR');
                                const horaFormatada = dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                return horaFormatada !== '00:00' ? `${dataFormatada} ${horaFormatada}` : dataFormatada;
                              }
                            }
                            return conta.data_pagamento;
                          } catch {
                            return conta.data_pagamento;
                          }
                        })()}
                      </div>
                    ) : (
                      <>
                        <div className="text-sm tabular-nums font-medium text-[color:var(--text-primary)]">
                          {vencimento ? vencimento.toLocaleDateString('pt-BR') : 'Data inválida'}
                        </div>
                        {vencimento && (
                          <div className={`text-xs mt-0.5 ${
                            diasVencimento < 0 ? 'text-[color:var(--status-danger-fg,var(--text-primary))]' : 
                            diasVencimento <= 7 ? 'text-[color:var(--status-warning-fg,var(--text-primary))]' : 
                            'text-[color:var(--text-muted)]'
                          }`}>
                            {formatarPrazoVencimento(diasVencimento, conta.status)}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top text-center">
                    <span className={`mx-auto inline-flex min-h-6 min-w-[132px] justify-center items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor}`}>
                      {statusInfo.icon}
                      <span>{statusInfo.label}</span>
                    </span>
                  </td>
                  {filtroAtivo === 'prontas_para_pagar' && (
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`inline-flex min-h-6 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${compactAuthorizationInfo.tone.bg} ${compactAuthorizationInfo.tone.text} ${compactAuthorizationInfo.tone.border}`}
                          title={compactAuthorizationInfo.details}
                          aria-label={`Autorização: ${compactAuthorizationInfo.label}. ${compactAuthorizationInfo.details}`}
                        >
                          {compactAuthorizationInfo.icon}
                          <span>{compactAuthorizationInfo.label}</span>
                        </span>
                        {authorization?.needs_second_authorization && authorization?.first_authorizer?.name && (
                          <span
                            className="text-[11px] text-[color:var(--text-muted)] truncate max-w-[160px]"
                            title={authorization?.first_authorizer?.email || authorization?.first_authorizer?.name}
                          >
                            1ª: {authorization.first_authorizer.name}
                          </span>
                        )}
                      </div>
                    </td>
                  )}
	                  <td className="px-2 py-2.5 text-sm text-[color:var(--text-primary)] text-center align-top">
	                    {renderArquivos(conta)}
	                  </td>
                  {filtroAtivo === 'prontas_para_pagar' && (
	                    <td className="px-2 py-2.5 align-top">
	                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                          {conta.status !== 'paga' && conta.status !== 'cancelada' && conta.status !== 'paga_parcialmente' && canAuthorizePayment && (
                            <button 
                              onClick={(event) => {
                                event.stopPropagation();
                                if (transactionInfo?.identifier) {
                                  abrirModalConfirmacao(conta);
                                }
                              }}
                              disabled={
                                estaProcessando || 
                                authorization?.status === 'processed' ||
                                !transactionInfo?.identifier ||
                                isSameUserFromFirstAuthorization
                              }
	                              className={`inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent transition-colors duration-200 ${
                                  isSameUserFromFirstAuthorization
                                    ? 'text-[color:var(--text-muted)] bg-[color:var(--surface-muted)] border-[color:var(--border)] cursor-not-allowed'
	                                : authorization?.status === 'cancelled'
	                                  ? 'text-[color:var(--status-warning-fg,var(--text-primary))] bg-[color:var(--status-warning-bg,var(--surface-muted))]/40 hover:bg-[color:var(--status-warning-bg,var(--surface-muted))] hover:border-[color:var(--status-warning-border,var(--border))]'
	                                  : transactionInfo?.identifier
	                                  ? 'text-[color:var(--status-good-fg,var(--text-primary))] bg-[color:var(--status-good-bg,var(--surface-muted))]/35 hover:bg-[color:var(--status-good-bg,var(--surface-muted))] hover:border-[color:var(--status-good-border,var(--border))]'
	                                  : 'text-[color:var(--status-good-fg,var(--text-primary))] bg-[color:var(--status-good-bg,var(--surface-muted))]/20 hover:bg-[color:var(--status-good-bg,var(--surface-muted))] hover:border-[color:var(--status-good-border,var(--border))]'
	                              } ${estaProcessando || authorization?.status === 'processed' ? 'opacity-50 cursor-not-allowed hover:border-transparent' : ''} ${focusRingClass}`}
                              title={
                                estaProcessando 
                                  ? 'Processando pagamento...' 
                                  : authorization?.status === 'processed'
                                  ? 'Pagamento já processado'
                                  : authorization?.status === 'cancelled'
                                  ? 'Pagamento falhou. Clique para tentar novamente'
                                  : !transactionInfo?.identifier
                                  ? `${transactionInfo?.fieldName || 'Identificador'} não encontrado`
                                  : isSameUserFromFirstAuthorization
                                  ? 'Você fez a 1ª autorização e não pode fazer a 2ª'
                                  : transactionInfo?.identifier
                                  ? `Pagar via ${getPaymentProviderLabel(conta)} (${transactionInfo.type?.toUpperCase()})`
                                  : 'Marcar como pago'
	                              }
                                aria-label={`Autorizar pagamento da conta ${conta.id}`}
	                            >
                              {estaProcessando ? (
                                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                </svg>
                              ) : (
                                <CheckCircle className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Versão Mobile - Cards com scroll interno */}
      <div className="md:hidden p-3">
        <div className="max-h-[68vh] overflow-y-auto overscroll-contain pr-1 space-y-3">
        {modoSelecao && (
          <div className="sticky top-0 z-10 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)]/95 backdrop-blur-sm p-3 shadow-sm">
            <label className="flex items-center gap-2 text-xs font-medium text-[color:var(--text-primary)]">
              <input
                type="checkbox"
                checked={todasContasSelecionaveisSelecionadas}
                onChange={(e) => handleSelecionarTodasFiltradas(e.target.checked)}
                className={`h-4 w-4 accent-[color:var(--accent)] border-[color:var(--border)] rounded ${focusRingClass}`}
              />
              Selecionar todas visíveis ({contasSelecionaveisFiltradas.length})
            </label>
          </div>
        )}
        {contasExibicao.length === 0 ? (
          <div className="text-center py-8 text-[color:var(--text-muted)]">
            <p className="text-sm">Nenhuma conta encontrada</p>
          </div>
        ) : (
          contasExibicao.map(conta => renderCardMobile(conta))
        )}
        </div>
      </div>
      
      {/* Loading Overlay padronizado */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[color:var(--bg)]/60 backdrop-blur-[1px]">
          <LoadingSpinner
            fullScreen={false}
            inline
            size="lg"
            label="Carregando dados..."
            subtitle="Aguarde enquanto atualizamos as contas."
          />
        </div>
      )}

      {/* Modal de Confirmação de Autorização */}
      {contaParaAutorizar && (() => {
        const transactionInfo = getTransactionIdentifier(contaParaAutorizar);
        const authorization = authorizations[contaParaAutorizar.id];
        const hasFirstAuthorization = !!authorization?.first_authorizer;
        const waitingSecondAuthorization = !!authorization?.needs_second_authorization && !authorization?.second_authorizer;
        const isFirstAuthorization = !authorization || (!hasFirstAuthorization && !waitingSecondAuthorization);
        const authorizationWarningMessage = isFirstAuthorization
          ? 'Esta é a primeira autorização. Uma segunda autorização será necessária para processar o pagamento.'
          : waitingSecondAuthorization
            ? `Esta é a segunda autorização. O pagamento será processado via ${getPaymentProviderLabel(contaParaAutorizar)} após a confirmação.`
            : `Esta ação irá processar o pagamento via ${getPaymentProviderLabel(contaParaAutorizar)}.`;
        
        return (
          <Modal
            isOpen={!!contaParaAutorizar}
            onClose={fecharModalConfirmacao}
            title="Confirmar Autorização de Pagamento"
            size="md"
          >
            <div className="space-y-5">
              {/* Aviso importante */}
              <div className="bg-[color:var(--status-warning-bg,var(--surface-muted))] border-l-4 border-[color:var(--status-warning-border,var(--border))] p-4 rounded">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[color:var(--status-warning-fg,var(--text-primary))] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[color:var(--status-warning-fg,var(--text-primary))]">
                      Você está prestes a autorizar um pagamento
                    </p>
                    <p className="text-sm text-[color:var(--status-warning-fg,var(--text-primary))] mt-1">
                      {authorizationWarningMessage}
                    </p>
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">Esta ação é irreversível.</p>
                  </div>
                </div>
              </div>

              {/* Resumo rápido */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Fornecedor</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1 truncate" title={contaParaAutorizar.fornecedor}>
                    {contaParaAutorizar.fornecedor || 'N/A'}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {formatarMoeda(parseFloat(contaParaAutorizar.valor) || 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Vencimento</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {contaParaAutorizar.data_vencimento_convertida 
                      ? contaParaAutorizar.data_vencimento_convertida.toLocaleDateString('pt-BR')
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Informações do pagamento */}
              <div className="bg-[color:var(--surface-muted)] rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--text-primary)]">
                  <CreditCard className="h-4 w-4" />
                  Informações do Pagamento
                </div>
                <div className="text-sm text-[color:var(--text-muted)]">
                  Tipo: <span className="font-semibold text-[color:var(--text-primary)] uppercase">{transactionInfo.type || 'N/A'}</span>
                </div>
                <div className="text-sm text-[color:var(--text-muted)]">
                  Identificador: <span className="font-mono text-[color:var(--text-primary)] text-xs break-all">{transactionInfo.identifier || 'N/A'}</span>
                </div>
              </div>

              {/* Status de autorização atual (se houver) */}
              {authorization && (
                <div className="bg-[color:var(--surface-muted)] rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-[color:var(--text-primary)] mb-2">Status de Autorização</h4>
                  <div className="flex flex-wrap gap-2">
                    {authorization.first_authorizer && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--status-info-bg,var(--surface-muted))] text-[color:var(--status-info-fg,var(--text-primary))]">
                        1ª: {authorization.first_authorizer.name}
                      </span>
                    )}
                    {authorization.second_authorizer && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--status-good-bg,var(--surface-muted))] text-[color:var(--status-good-fg,var(--text-primary))]">
                        2ª: {authorization.second_authorizer.name}
                      </span>
                    )}
                    {authorization.needs_second_authorization && !authorization.second_authorizer && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--status-warning-bg,var(--surface-muted))] text-[color:var(--status-warning-fg,var(--text-primary))]">
                        Aguardando 2ª autorização
                      </span>
                    )}
                    {authorization.status === 'processed' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--status-good-bg,var(--surface-muted))] text-[color:var(--status-good-fg,var(--text-primary))]">
                        Processado
                      </span>
                    )}
                    {authorization.status === 'cancelled' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[color:var(--status-danger-bg,var(--surface-muted))] text-[color:var(--status-danger-fg,var(--text-primary))]">
                        Falhou
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Botões de ação */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-[color:var(--border)]">
                <button
                  onClick={fecharModalConfirmacao}
                  disabled={processandoPagamento[contaParaAutorizar.id]}
                  className={`px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed ${focusRingClass}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarAutorizacao}
                  disabled={processandoPagamento[contaParaAutorizar.id]}
                  className={`px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] border border-transparent rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${focusRingClass}`}
                >
                  {processandoPagamento[contaParaAutorizar.id] ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Processando...
                    </>
                  ) : (
                    'Confirmar Autorização'
                  )}
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}

    </div>
  );
};

export default TabelaContasPagar;
